/** Usage chart component showing daily call volume, duration, and cost. */
import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface DailyUsage {
  date: string;
  total_calls: number;
  total_duration: number;
  total_cost: number;
}

interface UsageChartProps {
  data: DailyUsage[];
}

type MetricType = "calls" | "duration" | "cost";

/** Format seconds as "Xm Ys". */
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

/** Format number with commas. */
const formatNumber = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return "0";
  return new Intl.NumberFormat("en-US").format(value);
};

/** Custom tooltip for the chart. */
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const metric = payload[0].payload.metricType || "calls";

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        boxShadow: 3,
      }}
    >
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {payload.map((entry: any, index: number) => {
        const value = entry.value;
        let formattedValue: string;

        if (metric === "calls") {
          formattedValue = formatNumber(value);
        } else if (metric === "duration") {
          formattedValue = formatDuration(value);
        } else {
          formattedValue = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(value);
        }

        return (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color }}
            fontWeight="bold"
          >
            {entry.name}: {formattedValue}
          </Typography>
        );
      })}
    </Box>
  );
};

/**
 * UsageChart component.
 *
 * Displays daily usage data with three metrics:
 * - Call Volume (number of calls)
 * - Duration (total call duration)
 * - Cost (total cost in currency)
 *
 * Uses Recharts BarChart for visualization with tabs to switch metrics.
 */
const UsageChart: React.FC<UsageChartProps> = ({ data }) => {
  const [metric, setMetric] = useState<MetricType>("calls");
  const theme = useTheme();

  // G1: chart colors come from the theme so dark mode stays readable.
  const METRIC_COLORS = {
    calls: theme.palette.primary.main,
    duration: theme.palette.secondary.main,
    cost: theme.palette.success.main,
  } as const;

  // Transform data based on selected metric
  const chartData = data.map((item) => {
    const dateObj = new Date(item.date);
    const formattedDate = dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    let value: number;
    let name: string;

    switch (metric) {
      case "calls":
        value = item.total_calls;
        name = "Calls";
        break;
      case "duration":
        value = item.total_duration / 60; // Convert to minutes
        name = "Duration (min)";
        break;
      case "cost":
        value = item.total_cost;
        name = "Cost";
        break;
    }

    return {
      date: formattedDate,
      value,
      name,
      color: METRIC_COLORS[metric],
      metricType: metric,
    };
  });

  // Check if data is empty
  const isEmpty = data.length === 0 || data.every((d) => d.total_calls === 0);

  return (
    <Card elevation={2}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" component="h2">
            Daily Usage
          </Typography>
          <Tabs
            value={metric}
            onChange={(e, newValue) => setMetric(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 48 }}
          >
            <Tab label="Call Volume" value="calls" />
            <Tab label="Duration" value="duration" />
            <Tab label="Cost" value="cost" />
          </Tabs>
        </Box>

        {isEmpty ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={300}
          >
            <Typography variant="body1" color="text.secondary">
              No usage data for selected period
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
              />
              <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
              <YAxis stroke={theme.palette.text.secondary} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="value" fill={METRIC_COLORS[metric]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageChart;
